import { useEffect } from "react";

/**
 * Sets `document.title` to `<page> · ROOK` on mount and restores
 * the previous title on unmount. Satisfies CPF-UIR-102 (each route
 * shows a page-specific title in the browser tab + the back-forward
 * stack + bookmarks).
 *
 * The legacy Next portal got this for free via per-page `metadata`
 * exports. v3 ships as an SPA — there's no per-route head element,
 * so each view component calls this on mount to set its title.
 *
 * Pass JUST the page label ("Dashboard", "Users", "Logs"); the
 * hook appends " · ROOK" so the brand always shows in the
 * truncated tab + bookmark view.
 *
 * Restoring the previous title on unmount keeps the title sensible
 * during transitions — without it, switching from /home/logs to
 * /home/account would leave "Logs · ROOK" stuck on the tab for a
 * frame while Account mounts. The previous-title pointer lives in
 * a closure (not a global stack) so concurrent mounts don't fight.
 */
export const usePageTitle = (label: string): void => {
  useEffect(() => {
    const previous = document.title;
    document.title = `${label} · ROOK`;
    return () => {
      document.title = previous;
    };
  }, [label]);
};
